import { shallowEqual, useSelector } from 'react-redux';
import type { State as CompanyState } from '@/pages/CompanyReport/redux/interface';
import type { LocationState } from 'redux-first-router';
import { separateByThreeDigit } from '@simplywallst/formatting';
import { Row, Column } from '@simplywallst/ui-core';
import {
  DetailsRow,
  PrimaryLabel,
  SecondaryLabel,
  Label,
  Question,
  Link,
  TipContainer,
  OverLabel,
} from './styled';
import { ChevronRightIcon } from '@/pages/CompanyReport/components/Overview/components/Fundamentals/styled';
import { CompanyAnchor } from '@/constants/anchors';
import Tooltip from '@components/Tooltip';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import If from '@components/If';
import { pathConstructor } from '@/hooks/useCompany/utils';
import { useVisibleSectionsContext } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';
import { useCompanyReportPageUrl } from '@/pages/CompanyReport/hooks/useCompanyReportPageUrl';

const formatRatios = (ratio: number) => {
  if (Math.abs(ratio) > 9999) return `9,999x`;
  return `${separateByThreeDigit(ratio, Math.abs(ratio) > 999 ? 0 : 1)}x`;
};

interface State {
  company: CompanyState;
  location: LocationState;
}
type Variations =
  | 'profitable'
  | 'unprofitable'
  | 'prerevenue'
  | 'profitable-bank'
  | 'unprofitable-bank';
function selector(state: State) {
  try {
    let renderState: Variations = 'profitable';
    const path = pathConstructor(state.location);
    const companyId = state.company.urlToIdMapping[path];
    const analysis = state.company.analysis[companyId];
    const summary = state.company.summary[companyId];
    if (analysis.legacyChartData.pastNetIncome.length === 0) {
      renderState = 'prerevenue';
      return {
        PE: typeof analysis.PE === 'number' ? formatRatios(analysis.PE) : 'n/a',
        PS: 'n/a',
        PB: 'n/a',
        rawPB: analysis.PB,
        rawPE: analysis.PE,
        rawPS: analysis.PS,
        name: summary.tickerSymbol,
        renderState,
      };
    }
    const earningsEntry =
      analysis.legacyChartData.pastNetIncome.length > 0
        ? analysis.legacyChartData.pastNetIncome[
            analysis.legacyChartData.pastNetIncome.length - 1
          ]
        : [0, 0];
    const { 1: earnings } = earningsEntry;
    if (summary.industryData.primaryId === '7010000') {
      if (earnings < 0) {
        renderState = 'unprofitable-bank';
      } else {
        renderState = 'profitable-bank';
      }
    } else {
      if (earnings < 0) {
        renderState = 'unprofitable';
      } else {
        renderState = 'profitable';
      }
    }

    return {
      PB: formatRatios(analysis.PB),
      PE: formatRatios(analysis.PE),
      PS: formatRatios(analysis.PS),
      rawPB: analysis.PB,
      rawPE: analysis.PE,
      rawPS: analysis.PS,
      name: summary.tickerSymbol,
      renderState,
    };
  } catch {
    return undefined;
  }
}

export function DetailsColumn() {
  const selected = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);
  const getTerm = useGlossaryTerms();
  const peRatioTerm = getTerm('pe ratio');
  const psRatioTerm = getTerm('ps ratio');
  const pbRatioTerm = getTerm('pb ratio');
  const companyUrl = useCompanyReportPageUrl();
  const { loadSection } = useVisibleSectionsContext();
  if (typeof selected === 'undefined') {
    return null;
  }

  const showOver = (value: number) =>
    Number(value) > 9999 && <OverLabel>Over</OverLabel>;

  const handleValuationLink = () => {
    loadSection('valuation');
  };

  return (
    <>
      <DetailsRow>
        <If
          conditions={{
            profitable: selected.renderState === 'profitable',
            unprofitable: selected.renderState === 'unprofitable',
            prerevenue: selected.renderState === 'prerevenue',
            profitableBank: selected.renderState === 'profitable-bank',
            unprofitableBank: selected.renderState === 'unprofitable-bank',
          }}
        >
          {{
            profitable: () => (
              <>
                <Column col={6}>
                  <PrimaryLabel data-cy-id="fundamental-primary-value">
                    {showOver(selected.rawPE)}
                    {selected.PE}
                  </PrimaryLabel>
                  <Tooltip
                    openDelay={300}
                    light
                    title={<TipContainer>{peRatioTerm}</TipContainer>}
                  >
                    <Label data-cy-id="fundamental-primary-label">
                      P/E Ratio
                    </Label>
                  </Tooltip>
                </Column>
                <Column col={6} displayFlex alignItem="flex-end">
                  <div>
                    <SecondaryLabel data-cy-id="fundamental-secondary-value">
                      {showOver(selected.rawPS)}
                      {selected.PS}
                    </SecondaryLabel>
                    <Tooltip
                      openDelay={300}
                      light
                      title={<TipContainer>{psRatioTerm}</TipContainer>}
                    >
                      <Label data-cy-id="fundamental-secondary-label">
                        P/S Ratio
                      </Label>
                    </Tooltip>
                  </div>
                </Column>
              </>
            ),
            unprofitable: () => (
              <>
                <Column col={6}>
                  <PrimaryLabel data-cy-id="fundamental-primary-value">
                    {showOver(selected.rawPS)}
                    {selected.PS}
                  </PrimaryLabel>
                  <Tooltip
                    openDelay={300}
                    light
                    title={<TipContainer>{psRatioTerm}</TipContainer>}
                  >
                    <Label data-cy-id="fundamental-primary-label">
                      P/S Ratio
                    </Label>
                  </Tooltip>
                </Column>
                <Column col={6} displayFlex alignItem="flex-end">
                  <div>
                    <SecondaryLabel data-cy-id="fundamental-secondary-value">
                      {showOver(selected.rawPE)}
                      {selected.PE}
                    </SecondaryLabel>
                    <Tooltip
                      openDelay={300}
                      light
                      title={<TipContainer>{peRatioTerm}</TipContainer>}
                    >
                      <Label data-cy-id="fundamental-secondary-label">
                        P/E Ratio
                      </Label>
                    </Tooltip>
                  </div>
                </Column>
              </>
            ),
            prerevenue: () => (
              <>
                <Column col={6}>
                  <PrimaryLabel data-cy-id="fundamental-primary-value">
                    {showOver(selected.rawPE)}
                    {selected.PE}
                  </PrimaryLabel>
                  <Tooltip
                    openDelay={300}
                    light
                    title={<TipContainer>{peRatioTerm}</TipContainer>}
                  >
                    <Label data-cy-id="fundamental-primary-label">
                      P/E Ratio
                    </Label>
                  </Tooltip>
                </Column>
                <Column col={6} displayFlex alignItem="flex-end">
                  <div>
                    <SecondaryLabel data-cy-id="fundamental-secondary-value">
                      {showOver(selected.rawPS)}
                      {selected.PS}
                    </SecondaryLabel>
                    <Tooltip
                      openDelay={300}
                      light
                      title={<TipContainer>{psRatioTerm}</TipContainer>}
                    >
                      <Label data-cy-id="fundamental-secondary-label">
                        P/S Ratio
                      </Label>
                    </Tooltip>
                  </div>
                </Column>
              </>
            ),
            profitableBank: () => (
              <>
                <Column col={6}>
                  <PrimaryLabel data-cy-id="fundamental-primary-value">
                    {showOver(selected.rawPE)}
                    {selected.PE}
                  </PrimaryLabel>
                  <Tooltip
                    openDelay={300}
                    light
                    title={<TipContainer>{peRatioTerm}</TipContainer>}
                  >
                    <Label data-cy-id="fundamental-primary-label">
                      P/E Ratio
                    </Label>
                  </Tooltip>
                </Column>
                <Column col={6} displayFlex alignItem="flex-end">
                  <div>
                    <SecondaryLabel data-cy-id="fundamental-secondary-value">
                      {showOver(selected.rawPB)}
                      {selected.PB}
                    </SecondaryLabel>
                    <Tooltip
                      openDelay={300}
                      light
                      title={<TipContainer>{pbRatioTerm}</TipContainer>}
                    >
                      <Label data-cy-id="fundamental-secondary-label">
                        P/B Ratio
                      </Label>
                    </Tooltip>
                  </div>
                </Column>
              </>
            ),
            unprofitableBank: () => (
              <>
                <Column col={6}>
                  <PrimaryLabel data-cy-id="fundamental-primary-value">
                    {showOver(selected.rawPB)}
                    {selected.PB}
                  </PrimaryLabel>
                  <Tooltip
                    openDelay={300}
                    light
                    title={<TipContainer>{pbRatioTerm}</TipContainer>}
                  >
                    <Label data-cy-id="fundamental-primary-label">
                      P/B Ratio
                    </Label>
                  </Tooltip>
                </Column>
                <Column col={6} displayFlex alignItem="flex-end">
                  <div>
                    <SecondaryLabel data-cy-id="fundamental-secondary-value">
                      {showOver(selected.rawPS)}
                      {selected.PS}
                    </SecondaryLabel>
                    <Tooltip
                      openDelay={300}
                      light
                      title={<TipContainer>{psRatioTerm}</TipContainer>}
                    >
                      <Label data-cy-id="fundamental-secondary-label">
                        P/S Ratio
                      </Label>
                    </Tooltip>
                  </div>
                </Column>
              </>
            ),
          }}
        </If>
      </DetailsRow>
      <Row>
        <Column>
          <Question>{`Is ${selected.name} overvalued?`}</Question>
          <Link
            to={`${companyUrl}/${CompanyAnchor.COMPANY_VALUE_ANCHOR}`}
            onClick={handleValuationLink}
          >
            See Fair Value and valuation analysis
            <ChevronRightIcon />
          </Link>
        </Column>
      </Row>
    </>
  );
}
